import React from 'react';
import { CloseRounded, OpenInFullRounded } from '@mui/icons-material';
import { StemModuleImage } from '../../services/api';

interface Props {
  title: string | null;
  images: StemModuleImage[];
  onClose: () => void;
}

/**
 * Right-docked, scrollable panel of a STEM module's source pages — shown while
 * the student learns that module so they can follow the actual worksheet/comic/
 * circuit pages alongside the coach. Tap a page to open it full-res in a new
 * tab (no full-screen takeover of the chat).
 */
const StemImagePanel: React.FC<Props> = ({ title, images, onClose }) => (
  <aside className="hidden h-full w-[340px] flex-none flex-col border-l border-violet-200 bg-white/95 md:flex lg:w-[420px] xl:w-[480px]">
    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
      <div className="min-w-0">
        <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-violet-600">Module pages</p>
        <p className="truncate text-sm font-medium text-slate-800">{title || 'Curriculum'}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Hide module pages"
        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        <CloseRounded sx={{ fontSize: 18 }} />
      </button>
    </div>
    <p className="px-4 pt-2 text-[0.7rem] text-slate-400">Tap a page to open it full-size in a new tab.</p>
    <div className="flex-1 space-y-3 overflow-y-auto p-3">
      {images.map((im) => (
        <a
          key={im.page}
          href={im.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open page ${im.page} full-size`}
          className="group relative block w-full overflow-hidden rounded-lg border border-slate-200 shadow-sm transition hover:border-violet-400 hover:shadow-md"
        >
          <img src={im.url} alt={`Page ${im.page}`} loading="lazy" className="w-full" />
          <span className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition group-hover:opacity-100">
            <OpenInFullRounded sx={{ fontSize: 15 }} />
          </span>
        </a>
      ))}
    </div>
  </aside>
);

export default StemImagePanel;
